import { useEffect, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Copy, Trash2 } from 'lucide-react';
import { edTimeInput, edDateInput, isoFromEdmonton, edDayOfWeek, edParts, edAddDays } from '@/lib/tz';

const OPEN = '__OPEN__';
const pad = (n) => String(n).padStart(2, '0');
const toTime = (d) => edTimeInput(d);
const toDateStr = (d) => edDateInput(d);

// Interpret the picked calendar date + clock time as Edmonton wall time -> a UTC instant.
function combine(dateStr, timeStr) {
  return isoFromEdmonton(dateStr, timeStr);
}

// Create/edit a shift. `initial` may be an existing shift, or {staffId, date} for a new one.
export function ShiftModal({ open, onOpenChange, onSaved, staff, positions, showWages, initial, defaults }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [repeatUntil, setRepeatUntil] = useState('');
  const [repeatDays, setRepeatDays] = useState([]);

  useEffect(() => {
    if (!open || !initial) return;
    const dateStr = initial.startAt ? toDateStr(initial.startAt) : initial.date;
    const start = initial.startAt ? toTime(initial.startAt) : defaults?.start || '09:00';
    let end;
    if (initial.endAt) end = toTime(initial.endAt);
    else {
      const [h, m] = start.split(':').map(Number);
      const dur = defaults?.durationMin || 480;
      const e = new Date(2000, 0, 1, h, m + dur);
      end = `${pad(e.getHours())}:${pad(e.getMinutes())}`;
    }
    setForm({
      staffId: initial.staffId ?? null,
      date: dateStr,
      start,
      end,
      positionId: initial.positionId || '',
      breakMinutes: initial.breakMinutes ?? defaults?.breakMin ?? 30,
      breakPaid: initial.breakPaid ?? false,
      title: initial.title || '',
      notes: initial.notes || '',
      location: initial.location || '',
      openSlots: initial.openSlots || 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  if (!form) return null;

  const empWage = showWages ? staff.find((s) => s.id === form.staffId)?.hourlyWage : undefined;
  const startAt = combine(form.date, form.start);
  let endAt = combine(form.date, form.end);
  if (endAt <= startAt) endAt = isoFromEdmonton(edDateInput(edAddDays(startAt, 1)), form.end); // overnight → next Edmonton day
  const grossH = (endAt - startAt) / 3600000;
  const paidH = Math.max(0, grossH - (form.breakPaid ? 0 : (Number(form.breakMinutes) || 0) / 60));

  const save = async () => {
    setBusy(true);
    try {
      if (!editing && repeat) {
        if (!repeatUntil) { setBusy(false); return toast.error('Choose an end date for the repeat'); }
        const sp = edParts(startAt);
        const ep = edParts(endAt);
        const sm = sp.hour * 60 + sp.minute;
        let em = ep.hour * 60 + ep.minute;
        if (em <= sm) em += 1440;
        await staffApi.post('/scheduling/series', {
          staffId: form.staffId || null,
          positionId: form.positionId || null,
          frequency: repeatDays.length ? 'WEEKLY' : 'WEEKLY',
          interval: 1,
          weekdays: repeatDays.length ? repeatDays : [edDayOfWeek(startAt)],
          startDate: form.date,
          endDate: repeatUntil,
          startMinute: sm,
          endMinute: em,
          breakMinutes: Number(form.breakMinutes) || 0,
          breakPaid: form.breakPaid,
          title: form.title,
        });
        toast.success('Repeating shifts created');
        onSaved();
        onOpenChange(false);
        return;
      }
      const payload = {
        staffId: form.staffId || null,
        positionId: form.positionId || null,
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
        breakMinutes: Number(form.breakMinutes) || 0,
        breakPaid: form.breakPaid,
        title: form.title,
        notes: form.notes,
        location: form.location,
        openSlots: Number(form.openSlots) || 1,
      };
      if (editing) await staffApi.patch(`/scheduling/shifts/${initial.id}`, payload);
      else await staffApi.post('/scheduling/shifts', payload);
      toast.success(editing ? 'Shift updated' : 'Shift created');
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async () => {
    try {
      await staffApi.post(`/scheduling/shifts/${initial.id}/duplicate`, { dayOffset: 1 });
      toast.success('Shift duplicated to next day');
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    try {
      await staffApi.delete(`/scheduling/shifts/${initial.id}`);
      toast.success('Shift deleted');
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shift-modal" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit Shift' : 'Add Shift'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Employee</Label>
            <Select value={form.staffId || OPEN} onValueChange={(v) => setForm({ ...form, staffId: v === OPEN ? null : v })}>
              <SelectTrigger data-testid="shift-employee-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={OPEN}>Open Shift (unassigned)</SelectItem>
                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1.5 col-span-1">
              <Label>Date</Label>
              <Input type="date" data-testid="shift-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Start</Label>
              <Input type="time" data-testid="shift-start-input" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>End</Label>
              <Input type="time" data-testid="shift-end-input" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label>Role / Position</Label>
              <Select value={form.positionId || 'none'} onValueChange={(v) => setForm({ ...form, positionId: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="shift-position-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Break (minutes)</Label>
              <Input type="number" min="0" data-testid="shift-break-input" value={form.breakMinutes} onChange={(e) => setForm({ ...form, breakMinutes: e.target.value })} />
            </div>
          </div>

          <label className="flex items-center justify-between text-sm">
            <span>Break is paid</span>
            <Switch data-testid="shift-break-paid-switch" checked={form.breakPaid} onCheckedChange={(v) => setForm({ ...form, breakPaid: v })} />
          </label>

          {!form.staffId && (
            <div className="space-y-1.5">
              <Label>Employees required (open shift)</Label>
              <Input type="number" min="1" data-testid="shift-openslots-input" value={form.openSlots} onChange={(e) => setForm({ ...form, openSlots: e.target.value })} />
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Shift title (optional)</Label>
            <Input data-testid="shift-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Morning Kitchen" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label>Location (optional)</Label>
              <Input data-testid="shift-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Input data-testid="shift-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          {!editing && (
            <div className="rounded-lg border border-border/40 p-3 space-y-2">
              <label className="flex items-center justify-between text-sm">
                <span className="font-medium">Repeat this shift</span>
                <Switch data-testid="shift-repeat-switch" checked={repeat} onCheckedChange={setRepeat} />
              </label>
              {repeat && (
                <>
                  <div className="flex flex-wrap gap-1">
                    {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d, i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`repeat-day-${i}`}
                        onClick={() => setRepeatDays((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]))}
                        className={`w-8 h-8 rounded-full text-xs font-medium ${repeatDays.includes(i) ? 'bg-saffron text-white' : 'bg-bone text-muted-ink'}`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Repeat until</Label>
                    <Input type="date" data-testid="repeat-until-input" value={repeatUntil} onChange={(e) => setRepeatUntil(e.target.value)} />
                  </div>
                  <p className="text-[11px] text-muted-ink">Leave weekdays empty to repeat weekly on the shift&apos;s own day.</p>
                </>
              )}
            </div>
          )}

          <div className="rounded-lg bg-bone p-3 text-sm flex items-center justify-between" data-testid="shift-hours-summary">
            <span className="text-muted-ink">Paid hours</span>
            <span className="font-heading font-700 text-charcoal">{paidH.toFixed(2)} h</span>
          </div>
          {showWages && empWage != null && (
            <div className="text-xs text-muted-ink text-right">Est. cost: ${(paidH * empWage).toFixed(2)} @ ${empWage}/h</div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {editing && (
            <div className="flex gap-2 mr-auto">
              <Button variant="outline" size="sm" data-testid="shift-duplicate-btn" onClick={duplicate} className="gap-1.5"><Copy size={14} /> Duplicate</Button>
              <Button variant="outline" size="sm" data-testid="shift-delete-btn" onClick={remove} className="text-destructive gap-1.5"><Trash2 size={14} /> Delete</Button>
            </div>
          )}
          <Button data-testid="shift-save-btn" disabled={busy} onClick={save} className="bg-saffron hover:bg-saffron-hover">
            {busy ? 'Saving…' : editing ? 'Save Changes' : 'Create Shift'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
